import { api } from '@/lib/api.js';
import { inFlightDedup } from '@/lib/inFlight.js';

// MV-03 quote ingestion — parse drafts, triage inbox, review-approve.
// GUARD posture: approve is the ONLY call that turns a parse into a real
// quote; everything else here reads, stages drafts, or discards.

export function getQuoteInbox() {
  return inFlightDedup('quote-ingest:inbox', () =>
    api.get(`/api/tracon/quote-ingest/inbox?_t=${Date.now()}`)
  );
}

export function getDraftReview(quoteId) {
  return api.get(`/api/tracon/quote-drafts/${encodeURIComponent(quoteId)}/review?_t=${Date.now()}`);
}

export function approveDraft(quoteId, payload) {
  return api.post(`/api/tracon/quote-drafts/${encodeURIComponent(quoteId)}/approve`, payload);
}

export function discardDraft(quoteId, reason) {
  return api.post(`/api/tracon/quote-drafts/${encodeURIComponent(quoteId)}/discard`, { reason });
}

// Run (or re-run with force) the pipeline on an inbound document — used by
// the triage list after a manual deal assignment and by "Review parse" on
// detection-only watcher items.
export function runQuoteParse(docId, { force = false } = {}) {
  return api.post(`/api/tracon/quote-ingest/run/${encodeURIComponent(docId)}${force ? '?force=true' : ''}`, {});
}

// Manual deal assignment for a triage doc (then the caller re-runs the parse).
export function assignDocToDeal(docId, dealId) {
  return api.patch(`/api/tracon/document/${encodeURIComponent(docId)}`, {
    matched_deal_id: dealId,
    match_method: 'manual',
  });
}

// 198 §1.3: RE-FILING IS A NORMAL ACT, NOT A REPAIR.
// The door already existed (PATCH /api/tracon/document/:id validates against
// classifier.DOCUMENT_TYPES and writes BOTH confirmed_type and document_type);
// it had no handle on a filed row, so a file that landed as the wrong type
// stayed that way. Same handleless-door pattern as markRefunded and
// attachCarrierDoc.
export function refileDocumentType(docId, confirmedType) {
  return api.patch(`/api/tracon/document/${encodeURIComponent(docId)}`, {
    confirmed_type: confirmedType,
  });
}

// Front door A: upload a quote file against a deal and run the full pipeline
// (multipart, so raw fetch + JWT — the shared api wrapper hardcodes JSON).
export async function uploadQuoteFile(dealId, file) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('deal_id', dealId);
  fd.append('pipeline', 'quote');
  const res = await fetch(`/api/tracon/drop-pdf?_t=${Date.now()}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('tracon_token')}` },
    body: fd,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `quote upload failed (HTTP ${res.status})`);
  return body;
}

// MV-03 present flow: active quotes + options for the present sheet, and
// the curated-present write (checked options + ONE recommended + channels).
export function getPresentOptions(dealId) {
  return api.get(`/api/tracon/deals/${encodeURIComponent(dealId)}/present-options?_t=${Date.now()}`);
}

export function presentDealQuotes(dealId, payload) {
  return api.post(`/api/tracon/deals/${encodeURIComponent(dealId)}/present-quotes`, payload);
}

// Same-origin source-document bytes for the review screen's left pane.
// Returns an object URL the caller must revoke on unmount.
export async function fetchSourceBlobUrl(docId) {
  const res = await fetch(`/api/tracon/quote-ingest/doc/${encodeURIComponent(docId)}/source?_t=${Date.now()}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('tracon_token')}` },
  });
  if (!res.ok) throw new Error(`source fetch failed (HTTP ${res.status})`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
